/**
 * Browser-only sink that ships records to POST /api/log so client actions and
 * server activity end up interleaved in one file, in one timeline.
 *
 * Batched to keep the network quiet, flushed on page hide via sendBeacon so a
 * navigation away never loses the tail of a session.
 */

import { addSink, log, newCorrelationId, setCorrelationId, type LogRecord } from "./logger";

const ENDPOINT = "/api/log";
const FLUSH_MS = 2_000;
const MAX_BATCH = 50;

let installed = false;

export function installBrowserSink(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // One id per page session, stamped on every record so a browser action can be
  // traced into the server requests it caused.
  setCorrelationId(newCorrelationId());

  let queue: LogRecord[] = [];
  let timer: ReturnType<typeof setTimeout> | undefined;
  let shipping = false;

  function send(useBeacon: boolean): void {
    if (timer) {
      clearTimeout(timer);
      timer = undefined;
    }
    if (queue.length === 0) return;
    const batch = queue;
    queue = [];

    const body = JSON.stringify({ records: batch });

    if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }));
      return;
    }

    // `shipping` guards the recursion: a failed POST must not log an error that
    // re-enters this sink and triggers another POST.
    shipping = true;
    void fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    })
      .catch(() => {
        // Server-side logging is best-effort; the console sink already has it.
      })
      .finally(() => {
        shipping = false;
      });
  }

  addSink((record) => {
    if (shipping) return;
    queue.push(record);
    if (queue.length >= MAX_BATCH) {
      send(false);
      return;
    }
    timer ??= setTimeout(() => send(false), FLUSH_MS);
  });

  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") send(true);
  });
  window.addEventListener("pagehide", () => send(true));

  // Nothing else catches these; without them a crash leaves no trace on disk.
  window.addEventListener("error", (event) => {
    log.error("app", "window.error", {
      msg: event.message,
      source: event.filename,
      line: event.lineno,
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    log.error("app", "unhandled.rejection", { reason: event.reason });
  });
}
